import { useEffect } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  TripsRawEventName,
  useEmitCoreTripsRawEvent,
} from "@b2bportal/core-trips";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { HotelReservationPageProps } from "@tenant/hotels";
import { Loading, Page } from "@tenant/ui";
import clsx from "clsx";
import { CollapsableSection } from "./components/CollapsableSection";
import { HotelReservationPageHeader } from "./components/HotelReservationPageHeader";
import { HotelReservationPagePaymentDetails } from "./components/HotelReservationPagePaymentDetails";
import defaultStyles from "./HotelReservationPage.module.scss";
import { Merchandising } from "@/components/Merchandising";
import { formatDateWithTime, useFormatGuests } from "@/utils/format";
import { getLodgingCheckInTime, getLodgingCheckOutTime } from "@/utils/lodging";

const COMPONENT_KEY = CoreHotelsComponent.HotelReservationPage;

export const HotelReservationPage = ({
  context,
  className,
}: HotelReservationPageProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const { formatGuests } = useFormatGuests();

  const emitViewedEvent = useEmitCoreTripsRawEvent(
    TripsRawEventName.ViewedTripSummaryHotel
  );
  useEffect(() => {
    if (!context.isLoading && context.hotelReservation != null) {
      emitViewedEvent();
    }
  }, [context, emitViewedEvent]);

  const policiesSections =
    context.hotelReservation?.reservation?.lodgingData?.checkInInstructions?.policies?.map(
      (policy) => (
        <CollapsableSection title={policy.title}>
          <div className={cn("reservation-details")}>
            <div className={cn("detail-row")}>
              <div className={cn("detail-label")}>{policy.text}</div>
            </div>
          </div>
        </CollapsableSection>
      )
    );

  return (
    <Page
      className={clsx(
        block,
        className,
        cn("page-loading", {
          loading: context.isLoading || context.hotelReservation == null,
        })
      )}
    >
      <div className={cn("page-container")}>
        <div className={cn("page-content")}>
          {context.isLoading || context.hotelReservation == null ? (
            <div className={cn("loading")}>
              <Loading />
            </div>
          ) : (
            <>
              <HotelReservationPageHeader
                itinerary={context.hotelReservation}
              />
              <div className={cn("reservation-content")}>
                <CollapsableSection title="Dates" defaultOpen={true}>
                  <div className={cn("reservation-details")}>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-label")}>
                        {t("hotels.bookingConfirmation.checkInDate")}
                      </div>
                      <div className={cn("detail-value")}>
                        {formatDateWithTime(
                          context.hotelReservation.reservation.checkInDate,
                          getLodgingCheckInTime(
                            context.hotelReservation.reservation.lodgingData
                          )
                        )}
                      </div>
                    </div>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-label")}>
                        {t("hotels.bookingConfirmation.checkOutDate")}
                      </div>
                      <div className={cn("detail-value")}>
                        {formatDateWithTime(
                          context.hotelReservation.reservation.checkOutDate,
                          getLodgingCheckOutTime(
                            context.hotelReservation.reservation.lodgingData
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </CollapsableSection>
                <CollapsableSection
                  title={t(
                    "hotels.bookingConfirmation.reservationDetails.title"
                  )}
                  defaultOpen={true}
                >
                  <div className={cn("reservation-details")}>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-row-title")}>
                        {context.hotelReservation.reservation.roomInfo?.name}
                      </div>
                      <div className={cn("detail-row-value")}>
                        {context.hotelReservation.reservation.roomInfo?.beds?.bedTypes
                          ?.map(({ bedType }) => bedType)
                          .filter((type) => Boolean(type))
                          .join(", ")}
                      </div>
                    </div>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-label")}>
                        {t(
                          "hotels.bookingConfirmation.reservationDetails.primaryGuest"
                        )}
                      </div>
                      <div className={cn("detail-value")}>
                        {context.hotelReservation.reservation.guests[0].name ||
                          `${context.hotelReservation.reservation.guests[0].givenName} ${context.hotelReservation.reservation.guests[0].surname}`}
                      </div>
                    </div>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-label")}>
                        {t(
                          "hotels.bookingConfirmation.reservationDetails.numberOfGuests"
                        )}
                      </div>
                      <div className={cn("detail-value")}>
                        {formatGuests({
                          adultCount:
                            context.hotelReservation.reservation.adultCount,
                          childCount:
                            context.hotelReservation.reservation.childCount,
                          childrenAges:
                            context.hotelReservation.reservation.childrenAges,
                        })}
                      </div>
                    </div>
                    <div className={cn("detail-row")}>
                      <div className={cn("detail-label")}>
                        {t(
                          "hotels.bookingConfirmation.reservationDetails.confirmationCode"
                        )}
                      </div>
                      <div className={cn("detail-value")}>
                        {context.hotelReservation.reservation.reservationId}
                      </div>
                    </div>
                  </div>
                </CollapsableSection>
                <CollapsableSection title="Payment Details" defaultOpen={true}>
                  <HotelReservationPagePaymentDetails
                    itinerary={context.hotelReservation}
                  />
                </CollapsableSection>
                {policiesSections}
                <section className={cn("merchandising-section")}>
                  <h1 className={cn("merchandising-title")}>
                    {t("hotels.postbooking.confirmationPage.moreWaysToSave")}
                  </h1>
                  <div className={cn("merchandising-content")}>
                    <Merchandising
                      state={
                        context.hotelReservation.reservation.lodgingData?.state
                          .code
                      }
                      postalCode={
                        context.hotelReservation.reservation.lodgingData
                          ?.postalCode
                      }
                    />
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
};
